import { useEffect, useState } from "react";

const STORAGE_KEY = "groast-debug";

export function initialDebugState(search: string, stored: string | null): boolean {
  const params = new URLSearchParams(search);
  if (params.has("debug")) return params.get("debug") === "1";
  return stored === "1";
}

function isFormField(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable === true
  );
}

/** Dev-only debug overlay toggle. Backtick flips it (unless a form field is
 *  focused); ?debug=1/0 overrides; choice persists in localStorage.
 *  Hard-gated to DEV builds (pre-deploy audit): production users must not be able to
 *  reach the debug overlays via backtick/?debug=1 — parity with LoaderFxDebug's gating. */
export function useDebugMode(): boolean {
  const [debug, setDebug] = useState<boolean>(() => {
    let stored: string | null = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
    return initialDebugState(window.location.search, stored);
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "`") return;
      if (isFormField(e.target)) return;
      setDebug((d) => {
        const next = !d;
        try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
        return next;
      });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // DEV-only: in a production build this is a compile-time `false` — the overlay code
  // tree-shakes out of the render paths that gate on it.
  return import.meta.env.DEV && debug;
}
